import { ArrowLeft, DownloadCloud, RefreshCw } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  backfillInbox,
  clearSmsDefault,
  deleteInbox,
  facebookReauthorize,
  getFacebookLoginConfig,
  getWhatsappSignupConfig,
  importRingcentralHistory,
  listInboxes,
  patchInbox,
  refreshSenderHealth,
  registerInboxWebhook,
  setSmsDefault,
  updateEmailLimits,
  whatsappAuthorize,
} from '../../api/endpoints';
import type { Inbox, ManagedInbox } from '../../api/types';
import { Badge } from '../../components/Badge';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { Field } from '../../components/Field';
import { RefreshButton } from '../../components/RefreshButton';
import { Spinner } from '../../components/Spinner';
import { Toggle } from '../../components/Toggle';
import { WebsiteEmbed } from '../../components/WebsiteEmbed';
import {
  formFromInbox,
  toChannelPatch,
  validateForm,
  type ChannelForm,
  type FieldErrors,
} from '../../lib/channelForms';
import {
  channelHealth,
  identityLine,
  isManagedInbox,
  providerLine,
  reconnectViaOauth,
} from '../../lib/channelMeta';
import { FacebookIcon } from '../../lib/brandIcons';
import { CapabilitiesContext } from '../../lib/capabilities';
import { runFacebookLogin } from '../../lib/facebookLogin';
import { replaceQuestion, replacementChoices, senderName } from '../../lib/smsDefault';
import { useTimeFormat } from '../../lib/tenantTime';
import { useLoad } from '../../lib/useLoad';
import { runEmbeddedSignup } from '../../lib/whatsappSignup';
import { launchReconnect } from '../../oauth/launch';
import { useOauthReturn } from '../../oauth/useOauthReturn';
import { ChannelFields } from '../connect/fields';
import { usePublishHelpPage } from '../../helpchat/pageContext';
import './inbox.css';

/** Inbox settings: name + the four patchable behaviour fields. */
function SettingsCard({ inbox, onSaved }: { inbox: ManagedInbox; onSaved: () => void }) {
  const [name, setName] = useState(inbox.name);
  const [autoAssign, setAutoAssign] = useState(inbox.enableAutoAssignment);
  const [greetOn, setGreetOn] = useState(inbox.greetingEnabled);
  const [greeting, setGreeting] = useState(inbox.greetingMessage ?? '');
  const [lock, setLock] = useState(inbox.lockToSingleConversation);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    if (!name.trim()) {
      setError('The inbox needs a name.');
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await patchInbox(inbox.id, {
        name: name.trim(),
        enableAutoAssignment: autoAssign,
        greetingEnabled: greetOn,
        greetingMessage: greeting.trim() === '' ? null : greeting,
        lockToSingleConversation: lock,
      });
      setSaved(true);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card card-pad manage-card">
      <h2 className="manage-card-title">Inbox settings</h2>
      {error && <ErrorNote error={error} />}
      {saved && <Note tone="ok">Saved.</Note>}
      <Field label="Name">
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Toggle
        checked={autoAssign}
        onChange={setAutoAssign}
        title="Auto-assign conversations"
        sub="New conversations are spread across the inbox’s members."
      />
      <Toggle
        checked={greetOn}
        onChange={setGreetOn}
        title="Greeting message"
        sub="Sent automatically when a customer starts a conversation."
      />
      {greetOn && (
        <Field label="Greeting text">
          <textarea
            className="textarea"
            value={greeting}
            onChange={(e) => setGreeting(e.target.value)}
            placeholder="Thanks for reaching out — we’ll be right with you."
          />
        </Field>
      )}
      <Toggle
        checked={lock}
        onChange={setLock}
        title="Single ongoing conversation"
        sub="New messages continue the customer’s open conversation instead of starting a new one."
      />
      <div className="manage-card-actions">
        <button className="btn btn-primary" onClick={() => void save()} disabled={busy}>
          {busy && <Spinner />}
          Save settings
        </button>
      </div>
    </section>
  );
}

/**
 * Whether this number is the DEFAULT SMS SENDER — the one the dispatch system's
 * automated texts go out from (services/smsDefault.js). A switch that acts at
 * once behind a confirm, rather than a field in the settings form, because the
 * default is the whole tenant's: turning it on here takes it from another
 * number, and that is said before it happens.
 */
function SmsDefaultCard({
  inbox,
  inboxes,
  onSaved,
}: {
  inbox: ManagedInbox;
  inboxes: Inbox[];
  onSaved: () => void;
}) {
  const [confirming, setConfirming] = useState<'on' | 'off' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isDefault = inbox.defaultSmsSender === true;
  const current = inboxes.find((row) => row.defaultSmsSender && row.id !== inbox.id) ?? null;

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      if (confirming === 'on') await setSmsDefault(inbox.id);
      else await clearSmsDefault();
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change the default sender.');
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  };

  return (
    <section className="card card-pad manage-card">
      <h2 className="manage-card-title">Automated SMS</h2>
      <p className="manage-card-sub">
        The default sender is the one number the dispatch system’s automated texts go out from. There
        is one at a time.
      </p>
      {error && <ErrorNote error={error} />}
      <Toggle
        checked={isDefault}
        onChange={(next) => setConfirming(next ? 'on' : 'off')}
        title="Default for automated SMS"
        sub={
          isDefault
            ? 'Automated SMS is sent from this number.'
            : current
              ? `Automated SMS is sent from ${senderName(current)}.`
              : 'Automated SMS is off.'
        }
      />
      {confirming && (
        <ConfirmDialog
          title={confirming === 'on' ? 'Make this the default sender?' : 'Turn automated SMS off?'}
          body={
            <p>
              {confirming === 'off'
                ? 'Automated SMS will stop until you choose another number.'
                : current
                  ? replaceQuestion(current, identityLine(inbox))
                  : `Automated SMS will be sent from ${identityLine(inbox)}.`}
            </p>
          }
          confirmLabel={confirming === 'off' ? 'Turn off' : current ? 'Replace' : 'Make default'}
          danger={confirming === 'off'}
          busy={busy}
          onConfirm={() => void apply()}
          onCancel={() => setConfirming(null)}
        />
      )}
    </section>
  );
}

/**
 * Reauthorize a WhatsApp inbox by re-running Meta's embedded-signup popup —
 * fresh token, same number (the server rejects a mismatched pick). Renders
 * nothing on deployments without a platform Meta app; manual credential entry
 * stays available beside it either way.
 */
function WhatsappReauthorize({
  inbox,
  onSaved,
  onError,
}: {
  inbox: ManagedInbox & { channelType: 'whatsapp' };
  onSaved: () => void;
  onError: (message: string | null) => void;
}) {
  const signup = useLoad(getWhatsappSignupConfig);
  const [busy, setBusy] = useState(false);
  const config = signup.data;
  if (!config?.available) return null;

  const run = async () => {
    setBusy(true);
    onError(null);
    try {
      const result = await runEmbeddedSignup(config);
      if (result.status === 'done') {
        await whatsappAuthorize({
          inboxId: inbox.id,
          code: result.code,
          wabaId: result.wabaId,
          ...(result.phoneNumberId ? { phoneNumberId: result.phoneNumberId } : {}),
          ...(result.businessId ? { businessId: result.businessId } : {}),
        });
        onSaved();
      }
    } catch (e) {
      onError(e instanceof Error ? e.message : 'The re-authorization failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button className="btn btn-primary" onClick={() => void run()} disabled={busy}>
      {busy ? <Spinner /> : <FacebookIcon size={14} tint="currentColor" />}
      {inbox.channel.reauthorizationRequired
        ? 'Sign in with Facebook again'
        : 'Re-authorize with Facebook'}
    </button>
  );
}

/**
 * Reauthorize a Messenger inbox by re-running the Facebook dialog — fresh Page
 * token, same Page (the server refuses an account that does not manage it).
 * Also the token of every Instagram inbox riding on this Page, which is why an
 * Instagram inbox connected that way sends people here rather than to a
 * sign-in of its own. Renders nothing on deployments without a platform Meta
 * app; the pasted-token form stays available beside it either way.
 */
function FacebookReauthorize({
  inbox,
  onSaved,
  onError,
}: {
  inbox: ManagedInbox & { channelType: 'facebook' };
  onSaved: () => void;
  onError: (message: string | null) => void;
}) {
  const login = useLoad(getFacebookLoginConfig);
  const [busy, setBusy] = useState(false);
  const config = login.data;
  if (!config?.available) return null;

  const run = async () => {
    setBusy(true);
    onError(null);
    try {
      const result = await runFacebookLogin(config);
      if (result.status === 'done') {
        await facebookReauthorize({
          inboxId: inbox.id,
          userAccessToken: result.userAccessToken,
          pageId: inbox.channel.pageId,
        });
        onSaved();
      }
    } catch (e) {
      onError(e instanceof Error ? e.message : 'The re-authorization failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button className="btn btn-primary" onClick={() => void run()} disabled={busy}>
      {busy ? <Spinner /> : <FacebookIcon size={14} tint="currentColor" />}
      {inbox.channel.reauthorizationRequired ? 'Sign in with Facebook again' : 'Re-authorize with Facebook'}
    </button>
  );
}

/** Channel credentials: health, reconnect (OAuth) or re-credential (manual). */
function ChannelCardSection({
  inbox,
  onSaved,
  pageInbox = null,
}: {
  inbox: ManagedInbox;
  onSaved: () => void;
  /** For an Instagram inbox riding on a Page: the Messenger inbox whose sign-in refreshes it. */
  pageInbox?: Inbox | null;
}) {
  const [form, setForm] = useState<ChannelForm | null>(() => formFromInbox(inbox));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);
  const [probing, setProbing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [editing, setEditing] = useState(false);

  const health = channelHealth(inbox);
  const oauthReconnect = reconnectViaOauth(inbox);

  const reconnect = async () => {
    setBusy(true);
    setError(null);
    try {
      await launchReconnect(inbox);
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : 'Could not start the sign-in.');
    }
  };

  const saveCredentials = async () => {
    if (!form) return;
    const found = validateForm(form, { secretsOptional: true });
    setErrors(found);
    if (Object.keys(found).length) return;
    setProbing(true);
    setError(null);
    setSaved(false);
    try {
      await patchInbox(inbox.id, { channel: toChannelPatch(form) });
      setSaved(true);
      setEditing(false);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The credentials could not be saved.');
    } finally {
      setProbing(false);
    }
  };

  return (
    <section className="card card-pad manage-card">
      <div className="manage-channel-head">
        <div>
          <h2 className="manage-card-title">Channel</h2>
          <p className="manage-card-sub">
            {identityLine(inbox)} · {providerLine(inbox)}
          </p>
        </div>
        {health === 'connected' ? (
          <Badge tone="ok" dot>
            Connected
          </Badge>
        ) : (
          <Badge tone="warn" dot>
            Needs attention
          </Badge>
        )}
      </div>
      {error && <ErrorNote error={error} />}
      {saved && <Note tone="ok">Credentials updated.</Note>}
      {'reauthorizationRequired' in inbox.channel && inbox.channel.reauthorizationRequired && (
        <Note tone="warn">
          The provider stopped accepting the stored credentials —{' '}
          {oauthReconnect ? 'sign in again to restore the connection.' : 'enter fresh credentials below.'}
        </Note>
      )}
      {inbox.channelType === 'instagram' && inbox.channel.source === 'facebook_page' && (
        <Note tone="info">
          This account acts through its Facebook Page, so its access is the Page's: to refresh it,
          sign in with Facebook again on{' '}
          {pageInbox ? (
            <Link to={`/inboxes/${pageInbox.id}`}>{pageInbox.name}</Link>
          ) : (
            'the Page’s Messenger inbox'
          )}
          . There is no credential of its own to update here.
        </Note>
      )}
      {/* Only a provider mailbox has anything to reconnect: a manual IMAP one is always
          false here, by design, and the note would send its manager looking for a button. */}
      {'canModifyMailbox' in inbox.channel &&
        inbox.channel.canModifyMailbox === false &&
        inbox.channel.provider === 'google' && (
          <Note tone="warn">
            Stars, labels and deletes stay in the CRM for this mailbox — it was connected before Gmail
            write access was requested. Sign in with Google again to have them reach Gmail too.
          </Note>
        )}
      {'canModifyMailbox' in inbox.channel &&
        inbox.channel.canModifyMailbox === false &&
        inbox.channel.provider === 'microsoft' && (
          <Note tone="warn">
            This mailbox was connected the older way, over IMAP — which Outlook.com and many Microsoft 365
            mailboxes keep switched off — so stars, labels and deletes stay in the CRM. Sign in with
            Microsoft again to move it onto Microsoft&rsquo;s mail API.
          </Note>
        )}

      {probing ? (
        <div className="manage-probing">
          <Spinner />
          <span>Testing the connection… this can take up to a minute.</span>
        </div>
      ) : (
        <div className="manage-card-actions manage-card-actions-start">
          {oauthReconnect && (
            <button className="btn btn-primary" onClick={() => void reconnect()} disabled={busy}>
              <RefreshCw size={13} />
              {health === 'connected' ? 'Re-authorize' : 'Reconnect'}
            </button>
          )}
          {inbox.channelType === 'whatsapp' && (
            <WhatsappReauthorize inbox={inbox} onSaved={onSaved} onError={setError} />
          )}
          {inbox.channelType === 'facebook' && (
            <FacebookReauthorize inbox={inbox} onSaved={onSaved} onError={setError} />
          )}
          {form && !editing && (
            <button className="btn btn-secondary" onClick={() => setEditing(true)}>
              Update credentials
            </button>
          )}
        </div>
      )}

      {form && editing && !probing && (
        <div className="manage-credential-form">
          <Note tone="info">
            Leave a password or token blank to keep the stored one. Changed credentials are tested
            live before they’re saved.
          </Note>
          <ChannelFields form={form} errors={errors} onChange={setForm} />
          <div className="manage-card-actions">
            <button className="btn btn-secondary" onClick={() => setEditing(false)}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={() => void saveCredentials()}>
              Test & save
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

const BACKFILL_RANGES = [
  { value: 'all', label: 'Entire history' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last year' },
  { value: 'custom', label: 'Custom…' },
] as const;

/**
 * Import older emails — connecting a mailbox only syncs from ~24h before the
 * connect onward; this rewinds the IMAP cursor over the chosen window. Safe to
 * run repeatedly (already-imported mail is skipped by dedup).
 */
/**
 * Meta does not hand these out — we mint the verify token and derive the callback URL,
 * then register both against the WABA on connect. When that registration fails the
 * pair is all that stands between the tenant and a silent inbox, so both values stay
 * on screen with a retry: this card is the only place the manual path exists.
 */
function WhatsappWebhookCard({
  inbox,
  onSaved,
}: {
  inbox: ManagedInbox & { channelType: 'whatsapp' };
  onSaved: () => void;
}) {
  const { fmtDateTime } = useTimeFormat();
  const [copied, setCopied] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { webhookUrl, webhookVerifyToken, webhookRegisteredAt, webhookError, hasAppSecret } =
    inbox.channel;

  const copy = async (label: string, value: string) => {
    // The clipboard API is unavailable outside a secure context; the value is on
    // screen either way, so a failed copy must not surface as a broken button.
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
    } catch {
      setCopied(null);
    }
  };

  const retry = async () => {
    setBusy(true);
    setError(null);
    try {
      await registerInboxWebhook(inbox.id);
      // The endpoint answers 200 whether or not Meta accepted it — the outcome is on
      // the refreshed channel, so reload rather than reporting success here.
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The webhook could not be registered.');
    } finally {
      setBusy(false);
    }
  };

  const rows: [string, string | null][] = [
    ['Callback URL', webhookUrl],
    ['Verify token', webhookVerifyToken],
  ];

  return (
    <section className="card card-pad manage-card">
      <h2 className="manage-card-title">Meta webhook</h2>
      {webhookRegisteredAt ? (
        <Note tone="ok">
          Registered with Meta on {fmtDateTime(webhookRegisteredAt)}. Nothing to do — this WABA
          delivers <code>messages</code> and <code>smb_message_echoes</code> here.
        </Note>
      ) : (
        <>
          <p className="manage-card-sub">
            We could not enter these into Meta for you. Open your Meta app → WhatsApp →
            Configuration, paste both values, then subscribe the <code>messages</code> field. Add{' '}
            <code>smb_message_echoes</code> too if the team also replies from the WhatsApp Business
            app.
          </p>
          {webhookError && (
            <Note tone="warn">
              Automatic registration failed: {webhookError}. It usually means the system-user token
              is missing the <code>whatsapp_business_management</code> permission, or the business
              account has not been shared with the app.
            </Note>
          )}
        </>
      )}
      {error && <ErrorNote error={error} />}
      {!hasAppSecret && (
        <Note tone="warn">
          No app secret is stored for this channel. Meta signs every webhook with it, so inbound
          messages are rejected until you add it under Credentials above.
        </Note>
      )}
      {rows.map(([label, value]) => (
        <div className="webhook-row" key={label}>
          <span className="field-label">{label}</span>
          <code className="webhook-value">{value || '—'}</code>
          <button
            className="btn btn-secondary"
            disabled={!value}
            onClick={() => void copy(label, value ?? '')}
          >
            {copied === label ? 'Copied' : 'Copy'}
          </button>
        </div>
      ))}
      <div className="manage-card-actions manage-card-actions-start">
        <button className="btn btn-secondary" onClick={() => void retry()} disabled={busy}>
          {busy ? <Spinner /> : <RefreshCw size={13} />}
          {busy ? 'Registering…' : webhookRegisteredAt ? 'Register again' : 'Register with Meta'}
        </button>
      </div>
    </section>
  );
}

/**
 * Whether this mailbox is a safe place to send a campaign FROM.
 *
 * A mailbox connected this morning and one the company has used for ten years
 * look identical on every other screen — same adapter, same quota, same provider
 * default — and they are not the same risk. What gets a sender blocked is the
 * domain's authentication posture and a cold address suddenly sending volume, so
 * this card carries both halves and, beside them, the one control that acts on
 * the answer: the daily cap.
 *
 * It WARNS and never blocks. The operator knows things DNS cannot read — a domain
 * migrated last week is young to a resolver and old to everyone who has been
 * mailing it for years — so the suggestion is offered with a button rather than
 * applied. What it must never do is the reverse: `level: 'unknown'` renders as
 * "couldn't check" and never as a clean bill, because an operator who reads a
 * silent all-clear mails five thousand people from an address that cannot
 * authenticate itself.
 */
function SenderHealthCard({
  inbox,
  onSaved,
}: {
  inbox: ManagedInbox & { channelType: 'email' };
  onSaved: () => void;
}) {
  const { fmtDateTime } = useTimeFormat();
  const { senderHealth: health, senderHealthAt, email } = inbox.channel;
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cap, setCap] = useState<string>('');
  const [saved, setSaved] = useState(false);

  const recheck = async () => {
    setBusy(true);
    setError(null);
    try {
      await refreshSenderHealth(inbox.channel.channelId);
      // The endpoint writes the assessment onto the row; reload rather than
      // holding a second copy of it here, so the card and the campaign builder
      // are reading the same one.
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The check could not be run.');
    } finally {
      setBusy(false);
    }
  };

  const applyCap = async (value: number | null) => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updateEmailLimits(inbox.channel.channelId, { dailySendLimit: value });
      setSaved(true);
      setCap('');
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The limit could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const typed = cap.trim();
  const parsed = typed === '' ? null : Number.parseInt(typed, 10);
  const capValid = typed === '' || (Number.isInteger(parsed) && (parsed as number) >= 0);

  const tone = health
    ? ({ ok: 'ok', warn: 'warn', risk: 'warn', unknown: 'info' } as const)[health.level]
    : 'info';

  return (
    <section className="card card-pad manage-card">
      <h2 className="manage-card-title">Sending reputation</h2>
      <p className="manage-card-sub">
        What receivers can check about {email || 'this mailbox'} before they decide where a campaign
        lands. Nothing here stops a send — it is what to fix, and how hard to hold the mailbox back
        until you have.
      </p>

      {!health ? (
        <Note tone="info">
          Not checked yet. This runs shortly after a mailbox is connected and again every day; use
          Check now if you would rather not wait.
        </Note>
      ) : (
        <>
          <Note tone={tone}>
            {health.level === 'ok' && 'Nothing found. This mailbox looks safe to send volume from.'}
            {health.level === 'warn' && 'Worth a look before a large campaign.'}
            {health.level === 'risk' &&
              'A campaign from this mailbox is at real risk of being filtered or getting the mailbox blocked.'}
            {/* Never "looks fine": a check that could not run is its own answer,
                and the one thing this card must not do is round it to a pass. */}
            {health.level === 'unknown' &&
              'We could not finish checking this mailbox, so treat the result below as incomplete rather than as a clean bill.'}
          </Note>

          {health.domainAuth.findings.map((f) => (
            <Note key={f.id} tone={f.level === 'info' ? 'info' : 'warn'}>
              <strong>{f.title}.</strong> {f.detail}
            </Note>
          ))}

          <div className="sender-fact">
            <span className="field-label">Mailbox history</span>
            <span className="cmg-check-value">
              {health.mailboxAge.checked
                ? `Holds mail going back ${health.mailboxAge.label}`
                : `Could not be read — ${health.mailboxAge.reason || 'unknown reason'}`}
            </span>
          </div>
          <div className="sender-fact">
            <span className="field-label">Domain records</span>
            <span className="cmg-check-value">
              {health.domainAuth.checked
                ? health.domainAuth.findings.length
                  ? `${health.domainAuth.findings.length} to look at`
                  : 'SPF, DKIM and DMARC all present'
                : `Could not be read — ${health.domainAuth.reason || 'DNS did not answer'}`}
            </span>
          </div>
        </>
      )}

      {/**
       * The one control that acts on all of the above.
       *
       * Zero is accepted and means this mailbox sends nothing at all, which is
       * the honest bottom of the scale rather than a trap — an operator who wants
       * a mailbox connected for READING while its domain is fixed has no other
       * way to say so. Clearing the box restores the provider default.
       */}
      {/* Not `Field`, which wraps its children in a <label>: a button inside one
          is activated by a click meant for the input beside it. */}
      <span className="field-label" id={`cap-${inbox.id}`}>
        Daily sending limit
      </span>
      <div className="sender-cap-row">
        <input
          className="input"
          type="number"
          min={0}
          step={1}
          value={cap}
          aria-labelledby={`cap-${inbox.id}`}
          placeholder="Provider default"
          onChange={(e) => {
            setCap(e.target.value);
            setSaved(false);
          }}
        />
        <button
          className="btn btn-secondary"
          disabled={saving || !capValid}
          onClick={() => void applyCap(parsed)}
        >
          {saving ? <Spinner /> : null}
          {saving ? 'Saving…' : 'Save'}
        </button>
        {health?.suggestedDailyLimit ? (
          <button
            className="btn btn-secondary"
            disabled={saving}
            onClick={() => void applyCap(health.suggestedDailyLimit)}
          >
            Use {health.suggestedDailyLimit}/day
          </button>
        ) : null}
      </div>
      {capValid ? (
        <span className="field-hint">
          {health?.suggestedDailyLimit
            ? `Leave it empty for the provider's own allowance. For a mailbox in this shape, start around ${health.suggestedDailyLimit} a day and raise it over a few weeks. 0 stops sending from here entirely.`
            : "Leave it empty for the provider's own allowance. Any number is honoured, including 0, which stops sending from this mailbox entirely."}
        </span>
      ) : (
        <span className="field-error">Enter a whole number of 0 or more, or leave it empty.</span>
      )}
      {saved && <Note tone="ok">Saved.</Note>}
      {error && <ErrorNote error={error} />}

      <div className="manage-card-actions manage-card-actions-start">
        <button className="btn btn-secondary" onClick={() => void recheck()} disabled={busy}>
          {busy ? <Spinner /> : <RefreshCw size={13} />}
          {busy ? 'Checking…' : 'Check now'}
        </button>
        {senderHealthAt && (
          <span className="field-hint">Last checked {fmtDateTime(senderHealthAt)}</span>
        )}
      </div>
    </section>
  );
}

/**
 * Messenger and Instagram: the app-level webhook is the deployment's (entered once
 * in the Meta app dashboard), and what the connect does per channel is SUBSCRIBE
 * the Page or account to it. That subscription can fail — a token without
 * `pages_manage_metadata`, an account still in review — and a channel without it
 * sends perfectly while receiving nothing, so the outcome and a retry stay here.
 * A channel under the tenant's OWN app shows the routed URL and verify token that
 * app's dashboard must carry, because there the webhook is theirs to enter.
 */
function MetaWebhookCard({
  inbox,
  onSaved,
}: {
  inbox: ManagedInbox & { channelType: 'facebook' | 'instagram' };
  onSaved: () => void;
}) {
  const { fmtDateTime } = useTimeFormat();
  const [copied, setCopied] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { webhookUrl, webhookVerifyToken, webhookRegisteredAt, webhookError, hasAppSecret, ownApp } =
    inbox.channel;
  const instagram = inbox.channelType === 'instagram';
  const viaPage = instagram && inbox.channel.source === 'facebook_page';
  const noun = instagram ? 'account' : 'Page';
  const product = instagram ? 'Instagram' : 'Messenger';

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
    } catch {
      setCopied(null);
    }
  };

  const retry = async () => {
    setBusy(true);
    setError(null);
    try {
      await registerInboxWebhook(inbox.id);
      // 200 either way — the outcome is on the refreshed channel, not this call.
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The subscription could not be registered.');
    } finally {
      setBusy(false);
    }
  };

  const rows: [string, string | null][] = [
    ['Callback URL', webhookUrl],
    ['Verify token', webhookVerifyToken],
  ];

  return (
    <section className="card card-pad manage-card">
      <h2 className="manage-card-title">Meta webhook</h2>
      {webhookRegisteredAt ? (
        <Note tone="ok">
          {viaPage
            ? `The Page this account is linked to was subscribed to ${product} events on ${fmtDateTime(webhookRegisteredAt)}.`
            : `This ${noun} was subscribed to ${product} events on ${fmtDateTime(webhookRegisteredAt)}.`}{' '}
          Messages, receipts and replies typed in the {instagram ? 'Instagram app' : 'Page inbox'} arrive
          here.
        </Note>
      ) : (
        <>
          <p className="manage-card-sub">
            The {noun} is not subscribed to {product} events yet, so nothing arrives. Press{' '}
            <strong>Subscribe again</strong> once the cause below is fixed
            {viaPage ? ', or sign in with Facebook again on the Page’s Messenger inbox' : ''}.
          </p>
          {webhookError && (
            <Note tone="warn">
              Automatic subscription failed: {webhookError}.{' '}
              {instagram
                ? 'It usually means the account is not a professional (business or creator) account, or the app has not been granted message access to it yet.'
                : 'It usually means the Page token is missing the pages_manage_metadata permission, or the app is not yet allowed on that Page.'}
            </Note>
          )}
        </>
      )}
      {error && <ErrorNote error={error} />}
      {!hasAppSecret && (
        <Note tone="warn">
          No app secret is available for this channel. Meta signs every webhook with it, so inbound
          messages are rejected until you add it under Credentials above.
        </Note>
      )}
      {ownApp ? (
        <>
          <p className="manage-card-sub">
            This {noun} is under your own Meta app, so that app’s {product} webhook must carry these two
            values (Meta app → Webhooks → {instagram ? 'Instagram' : 'Page'} → Edit subscription), with
            the <code>messages</code>
            {instagram ? '' : ', message_echoes, message_deliveries, message_reads'} and{' '}
            <code>messaging_postbacks</code> fields subscribed.
          </p>
          {rows.map(([label, value]) => (
            <div className="webhook-row" key={label}>
              <span className="field-label">{label}</span>
              <code className="webhook-value">{value || '—'}</code>
              <button
                className="btn btn-secondary"
                disabled={!value}
                onClick={() => void copy(label, value ?? '')}
              >
                {copied === label ? 'Copied' : 'Copy'}
              </button>
            </div>
          ))}
        </>
      ) : (
        <p className="manage-card-sub">
          Inbound is delivered through the platform app’s {product} webhook — there is nothing to paste
          into Meta for this {noun}.
        </p>
      )}
      {instagram && inbox.channel.tokenExpiresAt && (
        <p className="manage-card-sub">
          The account’s access token is renewed automatically before it expires (currently{' '}
          {fmtDateTime(inbox.channel.tokenExpiresAt)}). If renewal ever fails the channel is marked as
          needing a sign-in.
        </p>
      )}
      {!viaPage && (
        <div className="manage-card-actions manage-card-actions-start">
          <button className="btn btn-secondary" onClick={() => void retry()} disabled={busy}>
            {busy ? <Spinner /> : <RefreshCw size={13} />}
            {busy ? 'Subscribing…' : webhookRegisteredAt ? 'Subscribe again' : 'Subscribe with Meta'}
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * Twilio's inbound URL is written for us on connect — onto the number, onto the
 * Messaging Service that fronts a whole sender pool, or onto the WhatsApp sender
 * itself. Any of them can legitimately fail (a token without the right permissions,
 * an unreachable host on a local deploy), and a channel that fails it sends
 * perfectly well while receiving nothing, so the URL and the retry stay on screen
 * until a registration succeeds.
 */
function TwilioWebhookCard({
  inbox,
  onSaved,
}: {
  inbox: ManagedInbox & { channelType: 'twilio' };
  onSaved: () => void;
}) {
  const { fmtDateTime } = useTimeFormat();
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { medium, phoneNumber, messagingServiceSid, twimlAppSid, voiceEnabled } = inbox.channel;
  const { webhookUrl, webhookRegisteredAt, webhookError, voiceHookupError } = inbox.channel;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const retry = async () => {
    setBusy(true);
    setError(null);
    try {
      await registerInboxWebhook(inbox.id);
      // 200 either way — the outcome is on the refreshed channel, not this call.
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The webhook could not be registered.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card card-pad manage-card">
      <h2 className="manage-card-title">Twilio webhook</h2>
      {webhookRegisteredAt ? (
        <>
          <Note tone="ok">
            Registered with Twilio on {fmtDateTime(webhookRegisteredAt)}. Inbound messages for{' '}
            <code>{messagingServiceSid || phoneNumber}</code> arrive here.
          </Note>
          {medium === 'whatsapp' && voiceEnabled && voiceHookupError && (
            <Note tone="warn">
              WhatsApp calling is not attached to this sender: {voiceHookupError}. Usually Meta
              refused it because the WhatsApp Business Account is below the 2,000-conversation
              tier or its business verification is still pending — once that clears, press{' '}
              <em>Register again</em>. Until then the softphone cannot place or take WhatsApp calls
              on this inbox.
            </Note>
          )}
        </>
      ) : (
        <>
          <p className="manage-card-sub">
            {medium === 'whatsapp' ? (
              <>
                Registering writes the URL below onto the WhatsApp sender itself. If it will not
                stick, open Twilio Console → Messaging → Senders → WhatsApp senders →{' '}
                <code>{phoneNumber}</code> and set the inbound webhook URL there.
                {voiceEnabled && twimlAppSid && (
                  <>
                    {' '}
                    For calls, attach TwiML app <code>{twimlAppSid}</code> under the sender&apos;s
                    Voice Endpoint Configuration — Meta only allows it once the account reaches the
                    2,000-conversation tier.
                  </>
                )}
              </>
            ) : messagingServiceSid ? (
              <>
                Open Twilio Console → Messaging → Services → <code>{messagingServiceSid}</code> →
                Integration, choose <em>Send a webhook</em>, paste the URL below, and turn{' '}
                <em>off</em> &ldquo;Use the webhook configured on the phone number&rdquo; — while
                that is on, the service&apos;s URL is ignored and nothing reaches this inbox.
              </>
            ) : (
              <>
                Open Twilio Console → Phone Numbers → Manage → Active numbers →{' '}
                <code>{phoneNumber}</code> and set &ldquo;A message comes in&rdquo; to the URL below.
              </>
            )}
          </p>
          {webhookError && (
            <Note tone="warn">
              Automatic registration failed: {webhookError}. Usually the auth token lacks access to
              this number or service, or Twilio could not be reached from this deployment.
            </Note>
          )}
        </>
      )}
      {error && <ErrorNote error={error} />}
      <div className="webhook-row">
        <span className="field-label">Inbound URL</span>
        <code className="webhook-value">{webhookUrl}</code>
        <button className="btn btn-secondary" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="manage-card-actions manage-card-actions-start">
        <button className="btn btn-secondary" onClick={() => void retry()} disabled={busy}>
          {busy ? <Spinner /> : <RefreshCw size={13} />}
          {busy ? 'Registering…' : webhookRegisteredAt ? 'Register again' : 'Register with Twilio'}
        </button>
      </div>
    </section>
  );
}

function BackfillCard({ inbox }: { inbox: ManagedInbox }) {
  const [range, setRange] = useState<(typeof BACKFILL_RANGES)[number]['value']>('90');
  const [customDays, setCustomDays] = useState('180');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ rewound: boolean; messagesToScan: number } | null>(null);

  const days =
    range === 'all' ? undefined : range === 'custom' ? Number(customDays) : Number(range);
  const daysValid = days === undefined || (Number.isFinite(days) && days > 0);

  const start = async () => {
    if (!daysValid) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await backfillInbox(inbox.id, days));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The import could not be started.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card card-pad manage-card">
      <h2 className="manage-card-title">Import older emails</h2>
      <p className="manage-card-sub">
        Connecting a mailbox only syncs mail from around that day onward. This imports older mail
        from the INBOX too — the mailbox’s own sent mail and archived mail are not included, and
        old threads will appear as open conversations.
      </p>
      {error && <ErrorNote error={error} />}
      {result &&
        (result.rewound ? (
          <Note tone="ok">
            Importing — up to <strong>{result.messagesToScan.toLocaleString()}</strong> messages
            will be scanned (already-imported mail is skipped). New conversations appear over the
            next minutes, oldest first, at roughly 500 messages a minute.
          </Note>
        ) : (
          <Note tone="info">Nothing new to import — that window is already covered.</Note>
        ))}
      <div className="manage-card-actions manage-card-actions-start">
        <select
          className="select"
          style={{ width: 180 }}
          value={range}
          onChange={(e) => {
            setRange(e.target.value as typeof range);
            setResult(null);
          }}
          disabled={busy}
        >
          {BACKFILL_RANGES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {range === 'custom' && (
          <input
            className="input"
            style={{ width: 110 }}
            type="number"
            min={1}
            value={customDays}
            onChange={(e) => setCustomDays(e.target.value)}
            disabled={busy}
            aria-label="Days to import"
            placeholder="Days"
          />
        )}
        <button
          className="btn btn-primary"
          onClick={() => void start()}
          disabled={busy || !daysValid}
        >
          {busy ? <Spinner /> : <DownloadCloud size={13} />}
          {busy ? 'Starting…' : 'Import'}
        </button>
      </div>
      {!daysValid && <p className="field-error">Days must be a positive number.</p>}
    </section>
  );
}

/**
 * Import older RingCentral history. Live capture starts when the channel connects
 * — the webhook from subscribe time, calls from the first call-log sync — so a
 * long-lived number arrives here with an empty past. Outbound SMS is the wider
 * gap: the webhook drops it entirely, so anything the team texted from the
 * RingCentral app or a desk phone only ever lands through this import.
 *
 * RC throttles the message store and call log hard, so the run walks one page at
 * a time in the background; the card polls its progress rather than waiting.
 */
function RingcentralImportCard({
  inbox,
  onSaved,
}: {
  inbox: ManagedInbox & { channelType: 'ringcentral' };
  onSaved: () => void;
}) {
  const { fmtDateTime } = useTimeFormat();
  const [range, setRange] = useState<(typeof BACKFILL_RANGES)[number]['value']>('90');
  const [customDays, setCustomDays] = useState('180');
  const [messages, setMessages] = useState(true);
  const [calls, setCalls] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = inbox.channel.historyImport;
  const running = run?.status === 'running';
  // What RC served vs what landed: the only thing that separates "RingCentral
  // does not keep that much history" from "we already had it" when an admin
  // asks for 90 days and counts fewer.
  const scanned = (run?.scanned?.messages ?? 0) + (run?.scanned?.calls ?? 0);
  const added = (run?.messages ?? 0) + (run?.calls ?? 0);
  const days =
    range === 'all' ? undefined : range === 'custom' ? Number(customDays) : Number(range);
  const daysValid = days === undefined || (Number.isFinite(days) && days > 0);

  // Progress only ever advances server-side, so a live run needs polling — there
  // is no socket event for it.
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(onSaved, 5000);
    return () => clearInterval(timer);
  }, [running, onSaved]);

  const start = async () => {
    if (!daysValid || (!messages && !calls)) return;
    setBusy(true);
    setError(null);
    try {
      await importRingcentralHistory(inbox.id, {
        ...(days === undefined ? {} : { days }),
        messages,
        calls,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The import could not be started.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card card-pad manage-card">
      <h2 className="manage-card-title">Import older messages and calls</h2>
      <p className="manage-card-sub">
        Pulls this number’s earlier RingCentral history into the CRM — texts sent and
        received from any device, plus the call log with its recordings. Old threads appear as
        open conversations. Running it again is safe: anything already here is skipped.
      </p>
      {!inbox.channel.connected && (
        <Note tone="warn">
          Sign in to RingCentral above first — the import needs a connected account.
        </Note>
      )}
      {error && <ErrorNote error={error} />}
      {run &&
        (run.status === 'running' ? (
          <Note tone="info">
            Importing — <strong>{run.messages.toLocaleString()}</strong> messages and{' '}
            <strong>{run.calls.toLocaleString()}</strong> calls added so far. This keeps running in
            the background; conversations fill in oldest first.
          </Note>
        ) : run.status === 'failed' ? (
          <Note tone="danger">
            The import stopped after adding {run.messages.toLocaleString()} messages and{' '}
            {run.calls.toLocaleString()} calls. Starting it again picks the window up from the top
            — nothing already imported is duplicated.
            {/* RingCentral's own words, on their own line: run into the sentence
                above they read as one broken thought, and the reason is the half
                an admin forwards to their RC administrator. */}
            {run.error && <span className="note-detail">{run.error}</span>}
          </Note>
        ) : (
          <Note tone="ok">
            Added <strong>{run.messages.toLocaleString()}</strong> messages and{' '}
            <strong>{run.calls.toLocaleString()}</strong> calls, finished{' '}
            {fmtDateTime(run.finishedAt ?? run.startedAt)}. RingCentral had{' '}
            {scanned.toLocaleString()} records in that window
            {added < scanned
              ? ' — the rest were already here, or belong to another number on the account'
              : ''}
            .
            {run.truncated &&
              ' The window held more than one run can walk, so the oldest records were left out — import them with a shorter window.'}
          </Note>
        ))}
      <Toggle
        checked={messages}
        onChange={setMessages}
        disabled={busy || running}
        title="Text messages"
        sub="Sent and received SMS. Photos and other MMS media are not imported."
      />
      <Toggle
        checked={calls}
        onChange={setCalls}
        disabled={busy || running}
        title="Call records"
        sub="Every logged call on this number, with recordings where RingCentral still has them."
      />
      <div className="manage-card-actions manage-card-actions-start">
        <select
          className="select"
          style={{ width: 180 }}
          value={range}
          onChange={(e) => setRange(e.target.value as typeof range)}
          disabled={busy || running}
        >
          {BACKFILL_RANGES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {range === 'custom' && (
          <input
            className="input"
            style={{ width: 110 }}
            type="number"
            min={1}
            value={customDays}
            onChange={(e) => setCustomDays(e.target.value)}
            disabled={busy || running}
            aria-label="Days to import"
            placeholder="Days"
          />
        )}
        <button
          className="btn btn-primary"
          onClick={() => void start()}
          disabled={
            busy || running || !daysValid || (!messages && !calls) || !inbox.channel.connected
          }
        >
          {busy || running ? <Spinner /> : <DownloadCloud size={13} />}
          {running ? 'Importing…' : busy ? 'Starting…' : 'Import'}
        </button>
      </div>
      {!daysValid && <p className="field-error">Days must be a positive number.</p>}
      {!messages && !calls && <p className="field-error">Pick messages, calls, or both.</p>}
    </section>
  );
}

function DangerCard({ inbox, inboxes }: { inbox: ManagedInbox; inboxes: Inbox[] }) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Disconnecting the default SMS sender must say what replaces it — the
  // server refuses the delete otherwise, so the dialog asks first.
  const isDefault = inbox.defaultSmsSender === true;
  const choices = isDefault ? replacementChoices(inboxes, inbox.id) : [];
  const [nextDefault, setNextDefault] = useState<number | 'off' | null>(null);

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteInbox(inbox.id, isDefault ? (nextDefault ?? undefined) : undefined);
      navigate('/');
    } catch (e) {
      setBusy(false);
      setConfirming(false);
      setError(e instanceof Error ? e.message : 'Could not delete the inbox.');
    }
  };

  return (
    <section className="card card-pad manage-card manage-danger">
      <h2 className="manage-card-title">Delete this inbox</h2>
      <p className="manage-card-sub">
        Disconnects the channel and permanently deletes every conversation in it — and, on a
        mailbox, every campaign ever sent from it along with its delivery record. This cannot be
        undone.
      </p>
      {error && <ErrorNote error={error} />}
      <div className="manage-card-actions manage-card-actions-start">
        <button className="btn btn-danger" onClick={() => setConfirming(true)}>
          Delete inbox
        </button>
      </div>
      {confirming && (
        <ConfirmDialog
          title={`Delete “${inbox.name}”?`}
          body={
            <>
              <p>
                The channel disconnects and <strong>all of its conversations are permanently
                deleted</strong> for everyone.
                {inbox.channelType === 'email' && (
                  <>
                    {' '}
                    So is <strong>every campaign sent from this mailbox</strong>, and the record of
                    who received them.
                  </>
                )}
              </p>
              {isDefault && (
                <fieldset className="sms-default-choice">
                  <legend>
                    This number is your default sender for automated SMS. What should send automated
                    SMS instead?
                  </legend>
                  {choices.map((row) => (
                    <label key={row.id} className="sms-default-option">
                      <input
                        type="radio"
                        name="next-sms-default"
                        checked={nextDefault === row.id}
                        onChange={() => setNextDefault(row.id)}
                        disabled={busy}
                      />
                      {senderName(row)}
                    </label>
                  ))}
                  <label className="sms-default-option">
                    <input
                      type="radio"
                      name="next-sms-default"
                      checked={nextDefault === 'off'}
                      onChange={() => setNextDefault('off')}
                      disabled={busy}
                    />
                    Turn automated SMS off
                  </label>
                </fieldset>
              )}
            </>
          }
          confirmLabel="Delete forever"
          danger
          requireText={inbox.name}
          blocked={isDefault && nextDefault === null}
          busy={busy}
          onConfirm={() => void remove()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </section>
  );
}

export function InboxManage() {
  const params = useParams();
  const id = Number(params['id']);
  const inboxes = useLoad(listInboxes);
  // The default SMS sender is the whole tenant's, so its switch also needs
  // Connect channels. Read off the context rather than useCapabilities(): this
  // screen also mounts where no provider is, and an unknown answer shows the
  // switch — the server is the boundary and answers in its own words.
  const capabilities = useContext(CapabilitiesContext);
  const mayChangeSmsDefault = capabilities?.me ? capabilities.me.access.features.connectChannels : true;
  const outcome = useOauthReturn();

  const found = inboxes.data?.find((row) => row.id === id) ?? null;
  // Every card below is a manage control, and the server sends this screen's
  // settings only to whoever manages the channel — so a reader who does not is
  // told so once, rather than shown a screen of blanks whose every save is
  // refused. `found && !inbox` is exactly that reader.
  const inbox = found && isManagedInbox(found) ? found : null;

  // What the assistant can see when someone asks "why is this channel not
  // receiving?" from this page. Health flags and registration state only —
  // the values that actually decide the answer, never credentials or contacts.
  usePublishHelpPage(
    inbox
      ? {
          title: 'Channel settings',
          entity: 'inbox',
          id: inbox.id,
          state: {
            channelType: inbox.channelType,
            health: channelHealth(inbox),
            autoAssign: inbox.enableAutoAssignment,
            ...('reauthorizationRequired' in inbox.channel
              ? { reauthorizationRequired: inbox.channel.reauthorizationRequired }
              : {}),
            ...(inbox.channelType === 'email'
              ? {
                  provider: inbox.channel.provider ?? 'imap/smtp',
                  imapEnabled: inbox.channel.imapEnabled,
                  smtpEnabled: inbox.channel.smtpEnabled,
                  verifiedForSending: inbox.channel.verifiedForSending,
                }
              : {}),
            ...(inbox.channelType === 'whatsapp'
              ? {
                  hasAppSecret: inbox.channel.hasAppSecret,
                  webhookRegisteredAt: inbox.channel.webhookRegisteredAt ?? 'never',
                  webhookError: inbox.channel.webhookError ?? 'none',
                  templateCount: inbox.channel.templateCount,
                }
              : {}),
            ...(inbox.channelType === 'twilio'
              ? {
                  medium: inbox.channel.medium,
                  voiceEnabled: inbox.channel.voiceEnabled,
                  webhookRegisteredAt: inbox.channel.webhookRegisteredAt ?? 'never',
                  webhookError: inbox.channel.webhookError ?? 'none',
                }
              : {}),
            ...(inbox.channelType === 'ringcentral'
              ? {
                  connected: inbox.channel.connected,
                  webhookSubscribed: inbox.channel.webhookSubscribed,
                  authType: inbox.channel.authType,
                }
              : {}),
            ...(inbox.channelType === 'facebook'
              ? {
                  source: inbox.channel.source,
                  webhookRegisteredAt: inbox.channel.webhookRegisteredAt ?? 'never',
                  webhookError: inbox.channel.webhookError ?? 'none',
                  instagramLinked: inbox.channel.instagramUsername ?? 'none',
                  humanAgentTag: inbox.channel.humanAgentTag,
                }
              : {}),
            ...(inbox.channelType === 'instagram'
              ? {
                  source: inbox.channel.source,
                  webhookRegisteredAt: inbox.channel.webhookRegisteredAt ?? 'never',
                  webhookError: inbox.channel.webhookError ?? 'none',
                  tokenExpiresAt: inbox.channel.tokenExpiresAt ?? 'does not expire',
                  humanAgentTag: inbox.channel.humanAgentTag,
                }
              : {}),
            ...(inbox.channelType === 'website'
              ? {
                  allowedOrigins: inbox.channel.allowedOrigins.length || 'any site',
                  identityProject: inbox.channel.firebaseProjectId ?? 'platform default',
                }
              : {}),
          },
        }
      : null,
  );

  return (
    <main className="page page-narrow">
      <Link to="/" className="back-link">
        <ArrowLeft size={14} />
        Channels
      </Link>

      {outcome && (
        <div className="home-banner">
          {outcome.kind === 'error' ? (
            <Note tone="danger">{outcome.message}</Note>
          ) : inbox && channelHealth(inbox) === 'connected' ? (
            <Note tone="ok">
              <strong>{identityLine(inbox)}</strong> is connected again.
            </Note>
          ) : inbox ? (
            <Note tone="warn">The sign-in didn’t finish — try the reconnect again.</Note>
          ) : null}
        </div>
      )}

      {inboxes.loading && !inboxes.data && (
        <div className="home-loading">
          <Spinner large />
        </div>
      )}
      {inboxes.error && <ErrorNote error={inboxes.error} />}
      {inboxes.data && !found && (
        <Note tone="warn">This inbox no longer exists. It may have been deleted.</Note>
      )}
      {found && !inbox && (
        <Note tone="warn">
          <strong>{found.name}</strong> is not yours to configure — you work this channel, but its
          settings and credentials belong to whoever manages it. Ask an administrator for Manage on
          it if you need them.
        </Note>
      )}

      {inbox && (
        <>
          <div className="page-head">
            <div>
              <h1 className="page-title">{inbox.name}</h1>
              <p className="page-sub">
                {identityLine(inbox)} · {providerLine(inbox)}
              </p>
            </div>
            {/* Connection health, webhook registration and import progress all
                move on their own — the same reload the cards run after a save. */}
            <RefreshButton
              refreshing={inboxes.refreshing}
              busy={inboxes.loading}
              onClick={inboxes.reload}
              title="Refresh this channel"
            />
          </div>
          <div className="manage-stack">
            <ChannelCardSection
              key={`c${inbox.id}`}
              inbox={inbox}
              onSaved={inboxes.reload}
              pageInbox={
                inbox.channelType === 'instagram' && inbox.channel.facebookPageId
                  ? (inboxes.data?.find(
                      (row) =>
                        row.channelType === 'facebook' &&
                        isManagedInbox(row) &&
                        row.channel.pageId === inbox.channel.facebookPageId,
                    ) ?? null)
                  : null
              }
            />
            {inbox.channelType === 'email' && (
              <SenderHealthCard key={`h${inbox.id}`} inbox={inbox} onSaved={inboxes.reload} />
            )}
            {inbox.channelType === 'email' && <BackfillCard key={`b${inbox.id}`} inbox={inbox} />}
            {inbox.channelType === 'ringcentral' && (
              <RingcentralImportCard key={`r${inbox.id}`} inbox={inbox} onSaved={inboxes.reload} />
            )}
            {inbox.channelType === 'whatsapp' && (
              <WhatsappWebhookCard key={`w${inbox.id}`} inbox={inbox} onSaved={inboxes.reload} />
            )}
            {inbox.channelType === 'twilio' && (
              <TwilioWebhookCard key={`t${inbox.id}`} inbox={inbox} onSaved={inboxes.reload} />
            )}
            {(inbox.channelType === 'facebook' || inbox.channelType === 'instagram') && (
              <MetaWebhookCard key={`m${inbox.id}`} inbox={inbox} onSaved={inboxes.reload} />
            )}
            {inbox.channelType === 'website' && <WebsiteEmbed key={`e${inbox.id}`} inbox={inbox} />}
            {mayChangeSmsDefault && (inbox.smsDefaultEligible || inbox.defaultSmsSender) && (
              <SmsDefaultCard
                key={`d${inbox.id}`}
                inbox={inbox}
                inboxes={inboxes.data ?? []}
                onSaved={inboxes.reload}
              />
            )}
            <SettingsCard key={`s${inbox.id}`} inbox={inbox} onSaved={inboxes.reload} />
            {/* Who may use the channel is NOT asked here. One person's level, calls
                and ownership are all one cell, and Settings → Team access edits that
                cell beside every other channel's — two screens writing it was two
                places to look and two ways to disagree. */}
            <DangerCard inbox={inbox} inboxes={inboxes.data ?? []} />
          </div>
        </>
      )}
    </main>
  );
}
